import React, { createContext, useContext, useEffect, useState } from "react";
import { UserManager, WebStorageStateStore, User } from "oidc-client-ts";
import axios from "axios";
import { decodeJWT } from "@/utils/jwt";

const AUTH_SERVER = process.env["DIO_AUTH_URL"] || "";
const CLIENT_ID = "spa-core-client";

// Helper function to decode JWT token


const oidcConfig = {
  authority: AUTH_SERVER,
  client_id: CLIENT_ID,
  redirect_uri: window.location.origin + "/checkout",
  response_type: "code",
  scope: "openid profile email",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  post_logout_redirect_uri: window.location.origin,
};

const userManager = new UserManager(oidcConfig);

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  signinRedirect: () => void;
  signoutRedirect: () => void;
  loginWithPassword: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const OidcAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const createManualSession = async (tokenData: any, email: string) => {
    try {
      await userManager.removeUser();
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = now + tokenData.expires_in;
      const decodedToken = tokenData.access_token ? decodeJWT(tokenData.access_token) : {};

      localStorage.setItem(`oidc.user:${AUTH_SERVER}:${CLIENT_ID}`, JSON.stringify({
        id_token: tokenData.id_token || '',
        session_state: tokenData.session_state || null,
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token || '',
        token_type: tokenData.token_type,
        scope: tokenData.scope,
        profile: {
          sub: decodedToken?.sub || email,
          email: decodedToken?.email || email,
          name: decodedToken?.name || decodedToken?.preferred_username || email,
        },
        expires_at: expiresAt
      }));

      return await userManager.getUser();
    } catch (error) {
      console.error('Error creating manual session:', error);
      return null;
    }
  };

  useEffect(() => {
    userManager.getUser().then((user) => {
      setUser(user);
      setIsLoading(false);
    }).catch(() => {
      setUser(null);
      setIsLoading(false);
    });

    userManager.events.addUserLoaded((user) => setUser(user));
    userManager.events.addUserUnloaded(() => setUser(null));
    userManager.events.addAccessTokenExpired(() => setUser(null));
    userManager.events.addSilentRenewError(() => setUser(null));
  }, []);

  const signinRedirect = () => userManager.signinRedirect();
  const signoutRedirect = () => userManager.signoutRedirect();
  
  const logout = async (): Promise<void> => {
    await userManager.removeUser();
    localStorage.removeItem('dio_access_token');
    localStorage.removeItem('dio_refresh_token');
    setUser(null);
  };
  
  const loginWithPassword = async (username: string, password: string): Promise<boolean> => {
    try {
      await userManager.removeUser();
      const tokenEndpoint = `${AUTH_SERVER}/protocol/openid-connect/token`;
      const formData = new URLSearchParams();
      formData.append('client_id', CLIENT_ID);
      formData.append('grant_type', 'password');
      formData.append('username', username);
      formData.append('password', password);
      formData.append('scope', 'openid profile email');
      const response = await axios.post(tokenEndpoint, 
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
        });
      
      if (response.status !== 200) {
        console.error('Login failed:', response.data);
        return false;
      }
      
      const tokenData = response.data;
      localStorage.setItem('dio_access_token', tokenData.access_token);
      if (tokenData.refresh_token) {
        localStorage.setItem('dio_refresh_token', tokenData.refresh_token);
      }
      
      const manualUser = await createManualSession(tokenData, username);
      
      if (manualUser) {
        setUser(manualUser);
        return true;
      }
      
      await userManager.clearStaleState();
      const loadedUser = await userManager.getUser();
      
      if (loadedUser) {
        setUser(loadedUser);
        return true;
      }
    
      window.location.reload();
      return true;
    } catch (error) {
      console.error('Password grant login error:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading, 
      signinRedirect, 
      signoutRedirect,
      loginWithPassword,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useOidcAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useOidcAuth must be used within OidcAuthProvider");
  return context;
}; 